<template>
  <div>
    <!-- Desktop Sidebar (≥960px only) -->
    <v-navigation-drawer
      v-if="!isMobile"
      class="rider-drawer"
      permanent
      app
      touchless
      :width="isMini ? 80 : 280"
      :mini-variant="isMini"
    >
      <div class="header-container">
        <div v-if="!isMini" class="logo-wrapper">
          <div class="logo-text">ELSONIII</div>
          <div class="logo-subtitle">Rider</div>
        </div>
        <div class="header-actions">
          <v-btn icon class="noti-btn" @click="$emit('open-notification')">
            <v-badge v-if="notificationCount > 0" :content="notificationDisplay" color="#059669" overlap>
              <v-icon color="#4b5563" size="22">mdi-bell-outline</v-icon>
            </v-badge>
            <v-icon v-else color="#4b5563" size="22">mdi-bell-outline</v-icon>
          </v-btn>
          <v-btn icon class="hamburger-btn" @click="isMini = !isMini">
            <v-icon color="#10B981" size="24">mdi-menu</v-icon>
          </v-btn>
        </div>
      </div>

      <v-list nav dense class="menu-list">
        <v-list-item
          v-for="item in menus"
          :key="item.to"
          :to="item.to"
          router
          link
          class="menu-item"
          :class="{ 'menu-item--active': isActive(item), 'menu-item--mini': isMini }"
        >
          <v-list-item-icon class="menu-icon">
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content v-if="!isMini">
            <v-list-item-title class="menu-title">{{ item.label }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <div v-if="!isMini" class="drawer-footer">
          <v-list dense class="pa-0">
            <v-list-item class="footer-item footer-profile">
              <v-avatar size="44" class="mr-3" rounded="lg">
                <img v-if="hasAvatar" :src="fullAvatarUrl" alt="avatar" @error="onAvatarError" />
                <v-avatar v-else size="44" color="#D1FAE5" rounded="lg">
                  <v-icon size="24" color="#10B981">mdi-account-circle</v-icon>
                </v-avatar>
              </v-avatar>
              <v-list-item-content>
                <div class="footer-item-title">{{ userName }}</div>
                <div class="footer-item-subtitle d-flex flex-wrap align-center">
                  <span class="mr-1">{{ vehicleInfo }}</span>
                  <div class="d-flex flex-wrap gap-1 mt-1">
                    <v-chip
                      v-for="s in riderServices"
                      :key="s.id"
                      x-small
                      class="service-chip"
                      color="#D1FAE5"
                      text-color="#059669"
                    >{{ s.serviceType?.name }}</v-chip>
                  </div>
                </div>
              </v-list-item-content>
              <v-btn icon small class="ml-2" @click="$emit('logout')">
                <v-icon size="22" color="#ef4444">mdi-logout</v-icon>
              </v-btn>
            </v-list-item>
          </v-list>
        </div>
        <div v-else class="drawer-footer-mini">
          <v-avatar size="40" class="mb-3" rounded="lg">
            <img v-if="hasAvatar" :src="fullAvatarUrl" alt="avatar" @error="onAvatarError" />
            <v-avatar v-else size="40" color="#D1FAE5" rounded="lg">
              <v-icon size="24" color="#10B981">mdi-account-circle</v-icon>
            </v-avatar>
          </v-avatar>
          <v-btn icon @click="$emit('logout')">
            <v-icon color="#ef4444" size="24">mdi-logout</v-icon>
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- Mobile Bottom Nav Bar -->
    <div v-if="isMobile" class="rbn-wrapper">
      <nav class="rbn">
        <router-link
          v-for="item in bottomMenus"
          :key="item.to"
          :to="item.to"
          class="rbn-item"
          :class="{ 'rbn-item--active': isActive(item) }"
        >
          <div class="rbn-icon-wrap">
            <v-icon size="22">{{ item.icon }}</v-icon>
          </div>
          <span class="rbn-label">{{ item.label }}</span>
        </router-link>

        <button class="rbn-item" @click="mobileDrawerOpen = true">
          <div class="rbn-icon-wrap">
            <v-icon size="22">mdi-dots-horizontal-circle-outline</v-icon>
          </div>
          <span class="rbn-label">เพิ่มเติม</span>
        </button>
      </nav>
    </div>

    <!-- Mobile Bottom Sheet (More menu) -->
    <v-bottom-sheet v-if="isMobile" v-model="mobileDrawerOpen" scrollable>
      <v-sheet class="rbn-sheet">
        <div class="rbn-sheet-handle"></div>

        <div class="rbn-sheet-profile">
          <v-avatar size="48" rounded="lg">
            <img v-if="hasAvatar" :src="fullAvatarUrl" alt="avatar" @error="onAvatarError" />
            <v-avatar v-else size="48" color="#D1FAE5" rounded="lg">
              <v-icon size="28" color="#10B981">mdi-account-circle</v-icon>
            </v-avatar>
          </v-avatar>
          <div class="rbn-sheet-profile-info">
            <div class="rbn-sheet-name">{{ userName }}</div>
            <div class="rbn-sheet-sub">{{ vehicleInfo }}</div>
          </div>
          <v-btn icon @click="mobileDrawerOpen = false">
            <v-icon color="#9CA3AF">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider class="mb-4"></v-divider>

        <div class="rbn-sheet-grid">
          <router-link
            v-for="item in extraMenus"
            :key="item.to"
            :to="item.to"
            class="rbn-sheet-item"
            :class="{ 'rbn-sheet-item--active': isActive(item) }"
            @click.native="mobileDrawerOpen = false"
          >
            <div class="rbn-sheet-icon">
              <v-icon size="24">{{ item.icon }}</v-icon>
            </div>
            <span class="rbn-sheet-label">{{ item.label }}</span>
          </router-link>
        </div>

        <div class="rbn-sheet-logout">
          <v-btn block outlined color="error" rounded @click="$emit('logout')" class="logout-btn">
            <v-icon left>mdi-logout</v-icon>
            ออกจากระบบ
          </v-btn>
        </div>
      </v-sheet>
    </v-bottom-sheet>
  </div>
</template>

<script>
import { getRiderProfileApi } from "@/services/api/auth";

export default {
  name: "SidebarRider",

  props: {
    notificationCount: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      isMini: false,
      avatarError: false,
      riderProfile: null,
      mobileDrawerOpen: false,
      menus: [
        { label: "แดชบอร์ด",      to: "/rider/dashboard",  icon: "mdi-view-dashboard-outline" },
        { label: "ประวัติการทำงาน", to: "/rider/history",   icon: "mdi-history" },
        { label: "กระเป๋าเงิน",   to: "/rider/wallet",     icon: "mdi-wallet-outline" },
        { label: "แจ้งปัญหา",     to: "/rider/tickets",    icon: "mdi-ticket-outline" },
        { label: "โปรไฟล์",       to: "/rider/profile",    icon: "mdi-account-circle-outline" },
      ],
      bottomMenus: [
        { label: "แดชบอร์ด",  to: "/rider/dashboard",  icon: "mdi-view-dashboard-outline" },
        { label: "ประวัติ",    to: "/rider/history",    icon: "mdi-history" },
        { label: "กระเป๋า",  to: "/rider/wallet",    icon: "mdi-wallet-outline" },
        { label: "โปรไฟล์",  to: "/rider/profile",    icon: "mdi-account-circle-outline" },
      ],
      extraMenus: [
        { label: "ประวัติการจัดส่ง", to: "/rider/history",  icon: "mdi-history" },
        { label: "แจ้งปัญหา",       to: "/rider/tickets",  icon: "mdi-ticket-outline" },
      ],
    };
  },

  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    userName() {
      if (!this.riderProfile) return "ชื่อผู้ใช้";
      const first = this.riderProfile.firstName || this.riderProfile.natId_firstName || "";
      const last  = this.riderProfile.lastName  || this.riderProfile.natId_lastName  || "";
      return `${first} ${last}`.trim() || this.riderProfile.username || "ชื่อผู้ใช้";
    },
    riderVehicle() {
      return this.riderProfile?.vehicles?.[0] || null;
    },
    vehicleInfo() {
      const v = this.riderVehicle;
      return v ? `${v.vehicleType} • ${v.vehiclePlate || ""}` : "Rider";
    },
    riderServices() {
      return this.riderVehicle?.services || [];
    },
    userAvatar() {
      return this.riderProfile?.profileImage || "";
    },
    fullAvatarUrl() {
      const src = this.userAvatar;
      if (!src) return "";
      if (src.startsWith("http")) return src;
      return `${this.$domain_api}${src}`;
    },
    notificationDisplay() {
      return this.notificationCount > 99 ? "99+" : this.notificationCount;
    },
    hasAvatar() {
      return !!this.userAvatar && !this.avatarError;
    },
  },

  async created() {
    await this.loadRiderProfile();
  },

  methods: {
    async loadRiderProfile() {
      try {
        const data = await getRiderProfileApi();
        this.riderProfile = data.rider || data.data || data;
      } catch (e) {
        console.error("[SidebarRider] load profile failed:", e);
      }
    },
    isActive(item) {
      return this.$route.path.startsWith(item.to);
    },
    onAvatarError() {
      this.avatarError = true;
    },
  },
};
</script>

<style scoped>
/* ─── Desktop Sidebar ─── */
.rider-drawer ::v-deep(.v-navigation-drawer__border) { display: none !important; }

.header-container { position: relative; padding: 20px 0 16px; }
.logo-wrapper { display: flex; flex-direction: column; align-items: center; gap: 2px; margin-top: 8px; margin-bottom: 8px; }
.logo-text { font-size: 23px; font-weight: 700; letter-spacing: 0.1em; color: #10B981; padding-top: 30px; }
.logo-subtitle { font-size: 14px; font-weight: 600; color: #6b7280; }
.header-actions { position: absolute; top: 6px; right: 12px; display: flex; align-items: center; gap: 4px; }

.menu-list { padding-top: 10px; }
.menu-item {
  position: relative; border-radius: 8px; margin: 6px 10px;
  padding-left: 8px; padding-right: 10px; min-height: 42px;
  color: #4b5563; transition: 0.2s;
}
.menu-item:hover { background: #D1FAE5; }
.menu-item--active { background: #10B981 !important; color: white !important; }
::v-deep .menu-item.menu-item--mini.menu-item--active {
  width: 40px; height: 40px; margin: 4px auto; padding: 0;
  border-radius: 10px; display: flex; justify-content: center; align-items: center;
}
.menu-icon { margin-right: 10px !important; }
.menu-title { font-size: 15px !important; font-weight: 600; }
.menu-item--mini { justify-content: center; padding-left: 0; padding-right: 0; }
.menu-item--mini .menu-icon { margin-right: 0 !important; justify-content: center; }

.drawer-footer { padding: 8px 6px 10px; }
.drawer-footer-mini { padding: 8px 0 10px; display: flex; flex-direction: column; align-items: center; }
.footer-item { min-height: 60px !important; margin: 0 10px !important; border-radius: 12px !important; transition: all 0.2s; border: 1px solid transparent; }
.footer-item:hover { background: #f8fafc; border-color: #e2e8f0; }
.footer-profile { background: #f8fafc; border: 1px solid #e2e8f0; margin-top: 10px !important; }
.footer-profile:hover { background: #f1f5f9; border-color: #cbd5e1; }
.footer-item-title { font-size: 15px !important; font-weight: 700; color: #1e293b; line-height: 1.2; }
.footer-item-subtitle { font-size: 13px !important; color: #64748b; margin-top: 2px; line-height: 1.2; }
.service-chip { font-size: 10px !important; font-weight: 700 !important; height: 18px !important; }
.gap-1 { gap: 4px; }

/* ─── Mobile Bottom Nav ─── */
.rbn-wrapper {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 200;
  background: transparent;
}

.rbn {
  display: flex;
  align-items: stretch;
  background: white;
  border-top: 1px solid #F3F4F6;
  box-shadow: 0 -2px 16px rgba(0, 0, 0, 0.07);
  /* Account for iOS safe area */
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

.rbn-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 8px 4px 6px;
  text-decoration: none;
  color: #9CA3AF;
  background: none;
  border: none;
  cursor: pointer;
  outline: none;
  font-family: 'Sarabun', 'Noto Sans Thai', sans-serif;
  transition: color 0.2s;
  -webkit-tap-highlight-color: transparent;
  min-height: 56px;
}

.rbn-item--active {
  color: #10B981;
}

.rbn-icon-wrap {
  width: 36px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  transition: background 0.2s, transform 0.15s;
  position: relative;
}

.rbn-item--active .rbn-icon-wrap {
  background: #ECFDF5;
  transform: translateY(-1px);
}

.rbn-label {
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
}

/* ─── Mobile "More" Sheet ─── */
.rbn-sheet {
  border-radius: 24px 24px 0 0 !important;
  padding: 0 16px calc(20px + env(safe-area-inset-bottom, 0px));
  font-family: 'Sarabun', 'Noto Sans Thai', sans-serif;
  max-height: 70vh;
  overflow-y: auto;
}

.rbn-sheet-handle {
  width: 40px;
  height: 4px;
  background: #E5E7EB;
  border-radius: 4px;
  margin: 12px auto 16px;
}

.rbn-sheet-profile {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 0 16px;
}
.rbn-sheet-profile-info { flex: 1; min-width: 0; }
.rbn-sheet-name { font-size: 16px; font-weight: 700; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rbn-sheet-sub  { font-size: 12px; color: #6B7280; margin-top: 2px; }

.rbn-sheet-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 20px;
}

.rbn-sheet-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 14px 8px;
  border-radius: 16px;
  background: #F9FAFB;
  text-decoration: none;
  color: #374151;
  transition: background 0.15s, transform 0.15s;
  -webkit-tap-highlight-color: transparent;
}
.rbn-sheet-item:active { transform: scale(0.96); }
.rbn-sheet-item--active { background: #ECFDF5; color: #059669; }

.rbn-sheet-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}
.rbn-sheet-item--active .rbn-sheet-icon { background: #D1FAE5; }

.rbn-sheet-label {
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  line-height: 1.2;
}

.rbn-sheet-logout { padding: 0 4px; }

.logout-btn {
  height: 48px !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  text-transform: none !important;
  border-radius: 14px !important;
  letter-spacing: 0 !important;
  font-family: 'Sarabun', 'Noto Sans Thai', sans-serif !important;
}
</style>
